import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, Clock, ArrowLeft, ArrowRight, Link2, Facebook, Twitter, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/site/Reveal";
import { NEWS_POSTS, getPostBySlug, formatDate } from "@/data/news";
import { Seo } from "@/components/site/Seo";

const NotFound = () => (
  <div data-testid="news-not-found">
    <section className="bg-gradient-to-br from-rda-teal/15 via-rda-offwhite to-rda-offwhite">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-24 text-center">
        <Reveal>
          <span className="inline-flex items-center rounded-full bg-rda-teal/10 px-4 py-1.5 text-sm font-semibold text-rda-teal">
            Post not found
          </span>
          <h1 className="mt-6 font-heading font-extrabold text-4xl text-rda-ink">
            We couldn't find that story
          </h1>
          <p className="mt-4 text-rda-muted">
            The article you're looking for may have moved. Browse our latest news instead.
          </p>
          <Button
            asChild
            className="mt-8 rounded-full bg-rda-teal text-white hover:bg-rda-tealDark font-semibold"
            data-testid="notfound-news-cta"
          >
            <Link to="/news">
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to News
            </Link>
          </Button>
        </Reveal>
      </div>
    </section>
  </div>
);

export default function NewsArticle() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);
  const [copied, setCopied] = useState(false);

  if (!post) return <NotFound />;

  const related = NEWS_POSTS.filter((p) => p.slug !== post.slug).slice(0, 2);

  const copyUrl = () => {
    const url = window.location.href;
    navigator.clipboard?.writeText(url).then(() => {
      setCopied(true);
      toast("Link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div data-testid="news-article-page">
      <Seo
        title={`${post.title} — RDA Glasgow`}
        description={post.excerpt}
        image={post.coverImage}
      />
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={post.coverImage} alt={post.coverAlt} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-rda-ink/90 via-rda-ink/60 to-rda-ink/40" />
        </div>
        <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <Link to="/news" className="inline-flex items-center text-sm text-white/80 hover:text-white" data-testid="article-back-link">
              <ArrowLeft className="mr-1.5 h-4 w-4" /> News & Stories
            </Link>
            <span className="mt-5 flex w-fit rounded-full bg-white/15 px-3 py-1 text-xs font-semibold text-white backdrop-blur-sm">
              {post.category}
            </span>
            <h1 className="mt-4 font-heading font-extrabold text-3xl sm:text-4xl md:text-5xl text-white leading-[1.1]">
              {post.title}
            </h1>
            <div className="mt-5 flex flex-wrap items-center gap-5 text-sm text-white/85">
              <span>By {post.author}</span>
              <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4" /> {formatDate(post.date)}</span>
              <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" /> {post.readingTime}</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Body */}
      <article className="bg-rda-offwhite">
        <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8 py-14 md:py-20">
          <Reveal>
            <div className="space-y-6 text-[17px] leading-relaxed text-rda-ink/85">
              {post.body.map((block, i) =>
                typeof block === "string" ? (
                  <p key={i}>{block}</p>
                ) : (
                  <blockquote
                    key={i}
                    className="my-8 border-l-4 border-rda-teal pl-6 font-heading text-2xl leading-snug text-rda-ink"
                  >
                    “{block.pullQuote}”
                  </blockquote>
                )
              )}
            </div>

            {/* Inline image */}
            <div className="my-10 overflow-hidden rounded-2xl shadow-soft">
              <img src={post.inlineImage} alt={post.inlineAlt} loading="lazy" className="w-full object-cover" />
            </div>

            {/* Share row */}
            <div className="flex items-center gap-3 border-t border-border pt-8" data-testid="article-share">
              <span className="text-sm font-semibold text-rda-ink">Share:</span>
              <button
                onClick={copyUrl}
                aria-label="Copy link"
                data-testid="article-share-copy"
                className="flex h-10 w-10 items-center justify-center rounded-full bg-white border border-border text-rda-ink hover:border-rda-teal hover:text-rda-teal transition-colors"
              >
                {copied ? <Check className="h-4 w-4" /> : <Link2 className="h-4 w-4" />}
              </button>
              <button
                onClick={copyUrl}
                aria-label="Share on Facebook (copies link)"
                data-testid="article-share-facebook"
                className="flex h-10 w-10 items-center justify-center rounded-full bg-white border border-border text-rda-ink hover:border-rda-teal hover:text-rda-teal transition-colors"
              >
                <Facebook className="h-4 w-4" />
              </button>
              <button
                onClick={copyUrl}
                aria-label="Share on Twitter (copies link)"
                data-testid="article-share-twitter"
                className="flex h-10 w-10 items-center justify-center rounded-full bg-white border border-border text-rda-ink hover:border-rda-teal hover:text-rda-teal transition-colors"
              >
                <Twitter className="h-4 w-4" />
              </button>
            </div>
          </Reveal>
        </div>
      </article>

      {/* Related posts */}
      <section className="bg-white border-t border-border" data-testid="article-related">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-rda-ink">More stories</h2>
          <div className="mt-8 grid gap-8 md:grid-cols-2">
            {related.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.08}>
                <Link
                  to={`/news/${p.slug}`}
                  className="group flex overflow-hidden rounded-2xl border border-border bg-rda-offwhite shadow-soft transition-all duration-300 hover:-translate-y-1.5 hover:shadow-lift"
                  data-testid={`related-post-${p.slug}`}
                >
                  <img src={p.coverImage} alt={p.coverAlt} loading="lazy" className="h-full w-32 shrink-0 object-cover" />
                  <div className="p-5">
                    <span className="text-xs font-semibold text-rda-teal">{p.category}</span>
                    <h3 className="mt-1.5 font-heading font-bold text-lg text-rda-ink leading-snug">{p.title}</h3>
                    <span className="mt-3 inline-flex items-center text-sm font-semibold text-rda-teal">
                      Read <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
